<template>
  <define-breadcrumbs v-slot="{ stickyBreadcrumbs }">
    <pf-page-breadcrumb v-if="name && !noBreadcrumbs" :sticky="stickyBreadcrumbs ? 'top' : undefined" :shadow-bottom="sticky">
      <pf-breadcrumb>
        <pf-breadcrumb-item :to="{ name: 'dashboard' }">
          Dashboard
        </pf-breadcrumb-item>
        <slot name="breadcrumbs" />
        <pf-breadcrumb-item active>
          {{ name }}
        </pf-breadcrumb-item>
      </pf-breadcrumb>
    </pf-page-breadcrumb>
  </define-breadcrumbs>

  <reuse-breadcrumbs v-if="!sticky && !bare" :sticky-breadcrumbs="true" />

  <pf-page-group v-if="pageTitle || $slots.header || (sticky && !bare && name && !noBreadcrumbs) || filter !== unset || $slots['toolbar-pre'] || $slots['toolbar-post']" :class="{ sticky }">
    <reuse-breadcrumbs v-if="sticky" />

    <pf-page-section v-if="pageTitle || $slots.header || filter !== unset || $slots['toolbar-pre'] || $slots['toolbar-post']" class="page-header" :shadow-bottom="sticky && !bare">
      <pf-toolbar v-if="filter !== unset || $slots['toolbar-pre'] || $slots['toolbar-post']">
        <pf-toolbar-content>
          <pf-toolbar-item style="flex-grow: 1">
            <pf-title h="1">{{ pageTitle }}</pf-title>
          </pf-toolbar-item>

          <slot name="toolbar-pre" />

          <pf-toolbar-item v-if="filter !== unset">
            <pf-search-input :model-value="filter" :placeholder="$t('Filtra')" @update:model-value="onInput" />
          </pf-toolbar-item>

          <slot name="toolbar-post" />
        </pf-toolbar-content>
      </pf-toolbar>

      <pf-title v-else-if="pageTitle" h="1">{{ pageTitle }}</pf-title>

      <slot name="header" />
    </pf-page-section>
  </pf-page-group>

  <component :is="pageTitle || $slots.header || (sticky && !bare && name && !noBreadcrumbs) || filter !== unset || $slots['toolbar-pre'] || $slots['toolbar-post'] ? PfPageGroup : PassThrough">
    <pf-alert v-if="failed && !ignoreFailed" variant="danger" inline :title="errorText" />

    <pf-page-section v-else-if="updating">
      <pf-empty-state>
        <pf-empty-state-header title="Loading" heading-level="h4">
          <template #icon>
            <pf-empty-state-icon>
              <pf-spinner />
            </pf-empty-state-icon>
          </template>
        </pf-empty-state-header>
        <pf-empty-state-body v-if="loadingText">
          {{ loadingText }}
        </pf-empty-state-body>
      </pf-empty-state>
    </pf-page-section>

    <pf-page-section v-else-if="empty">
      <pf-empty-state>
        <pf-empty-state-header :title="pageTitle" heading-level="h4" />
        <pf-empty-state-body v-if="emptyText">
          {{ emptyText }}
        </pf-empty-state-body>
      </pf-empty-state>
    </pf-page-section>

    <template v-else-if="!hide">
      <slot />
    </template>

    <div v-if="hide" v-show="(!failed || ignoreFailed) && !updating && !empty">
      <slot />
    </div>
  </component>
</template>

<style lang="scss" scoped>
.breadcrumb + .heading {
  h1,
  .h1,
  h2,
  .h2 {
    margin-top: 0;
  }
}

.sticky {
  position: sticky;
  top: 0;
  z-index: 2;
}

.sticky.no-breadcrumbs {
  top: 0;
}

.page-header :deep(.pf-v6-c-toolbar) {
  --pf-v6-c-toolbar--PaddingTop: 0;
  --pf-v6-c-toolbar--PaddingBottom: 0;
  --pf-v6-c-toolbar__content--PaddingLeft: 0;
  --pf-v6-c-toolbar__content--PaddingRight: 0;
}
</style>

<script lang="ts">
const unset = Symbol('unset');
</script>

<script lang="ts" setup>
import { $t as _t } from '@/i18n';
import { AsyncComputedState } from '@common/asyncComputed';
import debounce from 'lodash-es/debounce';
import { computed, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import PfPageGroup from '@vue-patternfly/core/components/Page/PageGroup.vue';
import PassThrough from '@vue-patternfly/core/helpers/PassThrough.vue';
import { createReusableTemplate } from '@vueuse/core';

export interface PageState {
  state: AsyncComputedState;
}

function isPageState(s: any): s is PageState {
  return typeof s === 'object' && typeof s.state === 'string';
}

type Props = {
  name?: string;
  title?: string;
  description?: string;
  hideBreadcrumbs?: boolean;
  bare?: boolean;
  empty?: boolean;
  emptyText?: string;
  loading?: boolean;
  loadingText?: string;
  error?: boolean;
  errorText?: string;
  filter?: string | typeof unset;
  state?: PageState | PageState[] | Record<string, PageState>;
  ignoreFailed?: boolean;
  sticky?: boolean;
  hide?: boolean;
}

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<Props>(), {
  filter: unset,
  emptyText: _t('Nessun elemento presente in questa sezione.'),
  loadingText: _t('Caricamento in corso'),
  errorText: _t('Si è verificato un problema inaspettato. Riprovare più tardi.'),
  state: () => [] as PageState[],
});

const emit = defineEmits<{
  (e: 'update:filter', value: string): void;
}>();

defineSlots<{
  default: (props?: Record<never, never>) => any;
  header: (props?: Record<never, never>) => any;
  breadcrumbs: (props?: Record<never, never>) => any;
  'toolbar-pre': (props?: Record<never, never>) => any;
  'toolbar-post': (props?: Record<never, never>) => any;
}>();

const [DefineBreadcrumbs, ReuseBreadcrumbs] = createReusableTemplate<{ stickyBreadcrumbs?: boolean }>();

const route = useRoute();

const pageTitle = computed(() => {
  return typeof props.title === 'undefined' ? props.name : props.title;
});

const updating = computed(() => {
  return props.loading || checkState(AsyncComputedState.Updating);
});

const failed = computed(() => {
  return props.error || checkState(AsyncComputedState.Error);
});

const noBreadcrumbs = computed(() => {
  return props.hideBreadcrumbs || props.bare || route.meta.disableNavigation;
});

watch(updating, (updating) => {
  nextTick(() => {
    document.body.dataset.ouiaPageSafe = updating ? 'false' : 'true';
  });
}, { immediate: true });

function checkState(stateValue: AsyncComputedState) {
  let states = props.state;
  if (!Array.isArray(states)) {
    if (isPageState(states)) {
      states = [states];
    } else {
      states = Object.values(states);
    }
  }
  return states.findIndex(v => v.state === stateValue) >= 0;
}

const onInput = debounce((filter: string) => {
  emit('update:filter', filter);
}, 300);
</script>
